import {FieldValues, FormControl, FormField, Option} from 'platform/components';

import {FieldPathByValue, UseFormReturn, useWatch} from 'react-hook-form';

import {isNotNil} from 'ramda';

import {VehicleTypeEnumObject} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {createVehicleTitle} from 'features/vehicle-catalogue';

import {useVehicleMake} from './hooks/useVehicleMake';
import {useVehicleModel} from './hooks/useVehicleModel';

interface VehicleTitleFields {
  formMake: string;
  formModelFamily: string;
  unknownMakes?: Option[];
  unknownModels?: Option[];
  variant?: string | null;
  title?: string | null;
}

interface VariantFieldProps<TFieldValues extends FieldValues & VehicleTitleFields>
  extends RequiredTestIdProps {
  control: FormControl<TFieldValues>;
  formApi: UseFormReturn<TFieldValues>;
  vehicleType: VehicleTypeEnumObject;
  variantName: FieldPathByValue<TFieldValues, string | Nullish>;
  onTitleChange: (title: string) => void;
}

export function VariantField<TFieldValues extends FieldValues & VehicleTitleFields>(
  props: VariantFieldProps<TFieldValues>
) {
  const {formMake, formModelFamily} = useWatch<TFieldValues>({
    control: props.control,
  });

  const {unknownMakes, unknownModels} = props.formApi.getValues();

  const {
    detail: {flatMakes},
  } = useVehicleMake({vehicleType: props.vehicleType, unknownMakes});

  const {
    detail: {flatModels},
  } = useVehicleModel({vehicleType: props.vehicleType, make: formMake, unknownModels});

  const handleChange = (newVariant: string | Nullish) => {
    const newTitle = createVehicleTitle({
      flatMakes,
      flatModels,
      formMake,
      formModel: formModelFamily,
      variant: newVariant,
    });

    if (isNotNil(newTitle)) {
      props.onTitleChange(newTitle);
    }
  };

  return (
    <FormField
      control={props.control}
      type="text"
      name={props.variantName}
      label={i18n.t('entity.vehicle.labels.variantName')}
      onChange={handleChange}
      data-testid={suffixTestId('variant', props)}
    />
  );
}
